import Button from 'tio-ui/components/button';
import Flatpickr from '../../../flatpickr';
import { Section } from 'tio-ui/components/layout';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import { t } from 'ember-intl';
import type IntlService from 'ember-intl/services/intl';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { TasProgramInstanceExtension as TasProgramInstanceModel } from 'tio-data/data/resources/tas-program-instance.ext';
import { PhPencil, PhX, PhCalendarBlank } from 'ember-phosphor-icons';
import type { CustomFieldSignature, CustomFieldOption } from '../../../types/tuition-assistance.ts';
import { setSemesterCodeString } from '../../../utils/format.ts';
import { formatDateToISO, formatISODateToDisplay } from '../../../utils/date/index.ts';
import { trackedObject } from '@ember/reactive/collections';
import { dropTask } from 'ember-concurrency';
import type { Store } from '@warp-drive/core';
import type Inputmask from 'inputmask';
import type SessionContextService from '../../../services/session-context.ts';
import type TuitionAssistanceService from '../../../services/tuition-assistance.ts';
import {
  getSingleValueForTasField,
  setSingleValueForTasField,
  isTasFieldComponentType,
  type TemplateFieldName,
} from '../../../utils/tuition-assistance/fields.ts';
import { query } from '@warp-drive/utilities/json-api';
import { saveExistingRecordWithChangelog } from 'tio-data/requests/save-record';
import * as v from 'valibot';
import type { ValidationErrorSignature } from '../../tuition-assistance/forms/courses/grade-select-options.ts';
import Input from 'tio-ui/components/input';
import Textarea from 'tio-ui/components/textarea';
import ReSelect from 'tio-ui/components/select';
import { or, not } from 'tio-ui/utilities';
import inputmask from '../../../modifiers/inputmask.ts';
import TuitionAssistanceProgramFormField from '../../tuition-assistance/program-form-field.gts';
import TuitionAssistanceFormsCustomFieldsForProvince from '../../tuition-assistance/forms/custom-fields-for-province.gts';
import SchoolSelect from '../../tuition-assistance/program-details/school-select.gts';
import type { TasUnifiedInstitution as TasUnifiedInstitutionModel } from 'tio-data/data/resources/tas-unified-institution.type';
import type { TasUnifiedInstitution } from 'tio-data/data/resources/search.ext';
import {
  validateBeginDate,
  validateEndDate,
  type FieldConfig,
} from '../../../utils/tuition-assistance/course-date-validation.ts';
import errorsForValibotField from '../../../helpers/errors-for-valibot-field.ts';
import ExpandableText from '../../expandable-text.gts';

// Used throughout the component to type-cast programTemplate.fields
type ProgramTemplateFields =
  Record<string, { visible?: boolean; label?: string; required?: boolean }> | undefined;

// DOM target of an inputmask-bound element. `userOptions` exists on the
// runtime Instance but is not part of @types/inputmask, so we narrow it here.
type InputmaskTarget = HTMLInputElement & {
  inputmask?: Inputmask.Instance & { userOptions?: { alias?: string } };
};

/**
 * Configuration for editable program instance fields
 *
 * NOTE: SCHOOL_INSTITUTION_NAME component selection is resolved via isTasFieldComponentType,
 * not this static config.
 */
const PROGRAM_INSTANCE_FIELD_CONFIGS = [
  { name: 'SCHOOL_INSTITUTION_NAME', type: 'text', testId: 'school-name' },
  { name: 'PROGRAM_SPECIALIZATION', type: 'text', testId: 'specialization' },
  { name: 'PROGRAM_MAJOR', type: 'text', testId: 'major' },
  { name: 'ESTIMATED_PROGRAM_BEGIN', type: 'date', testId: 'program-begin' },
  { name: 'ESTIMATED_PROGRAM_COMPLETION', type: 'date', testId: 'program-completion' },
  { name: 'ESTIMATED_COST', type: 'currency', testId: 'estimated-cost' },
  { name: 'ENROLLMENT_OBJECTIVE', type: 'text', testId: 'enrollment-objective' },
] as const;

/**
 * Configuration for editable application fields
 */
const APPLICATION_FIELD_CONFIGS = [
  { name: 'COURSES_BEGIN_DATE', type: 'date', testId: 'courses-begin', hasValidation: true },
  { name: 'COURSES_END_DATE', type: 'date', testId: 'courses-end', hasValidation: true },
  { name: 'COURSES_RECEIPT_DATE', type: 'date', testId: 'courses-receipt' },
  { name: 'SEMESTER_CODE', type: 'text', testId: 'semester-code' },
] as const;

interface ApplicationDetailsSectionSignature {
  Args: {
    model: TasApplicationModel;
    /** Prevents admin-only application edits for non-approver views. */
    isReadOnly: boolean;
    /** Edit-mode flag — owned by the parent page because ApprovalFooter also reads it */
    isEditingApplicationDetails: boolean;
    onEditingApplicationDetailsChange: (value: boolean) => void;
    /**
     * Tracked copy of application fields during edit — owned by the parent page
     * because syncScholarshipsFromApplication (scholarship drawer) writes into it
     */
    applicationFieldsCopy: TasApplicationModel['fields'] | null;
    onApplicationFieldsCopyChange: (fields: TasApplicationModel['fields'] | null) => void;
    /**
     * Pushes GPA-derived approved totals (raw cents) into the parent's
     * financial-cluster editableApplicationTotal tracked field
     */
    onApplicationTotalChange: (value: number) => void;
    /** Route for the Program row link — depends on the consuming app, owned by the parent page */
    programTemplateRoute?: string;
    /** Shared custom-field merge helper — also used by the parent's course-editing cluster */
    updateFieldsWithNewValues: (
      existingFields: CustomFieldSignature[],
      updatedFields: CustomFieldSignature[]
    ) => CustomFieldSignature[];
  };
}

export default class ApplicationDetailsSection extends Component<ApplicationDetailsSectionSignature> {
  @service declare sessionContext: SessionContextService;
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare tuitionAssistance: TuitionAssistanceService;

  // Inline application details editing
  // (isEditingApplicationDetails and applicationFieldsCopy live on the parent page —
  // see Args — because ApprovalFooter and the scholarship drawer also use them)
  @tracked applicationCustomFieldsCopy: CustomFieldSignature[] | null = null;
  @tracked programInstanceFieldsCopy: TasProgramInstanceModel['fields'] | null = null;
  @tracked programInstanceCustomFieldsCopy: CustomFieldSignature[] | null = null;
  @tracked applicationDetailsChangelog = '';
  @tracked selectedSchool?: TasUnifiedInstitutionModel;
  @tracked hasSubmittedApplicationDetails = false;
  @tracked courseDateValidationErrors: Record<string, readonly string[]> = {};

  /**
   * Get custom fields for EMPLOYEE province from program instance
   * In edit mode, uses tracked copy; otherwise uses live model data
   * Sorted by ordinal for consistent display
   */
  get customEmployeeFields(): CustomFieldSignature[] {
    const fields: CustomFieldSignature[] =
      this.args.isEditingApplicationDetails && this.programInstanceCustomFieldsCopy
        ? this.programInstanceCustomFieldsCopy.filter((field) => field.province === 'EMPLOYEE')
        : (this.args.model.tasProgramInstance?.customFields || []).filter(
            (field) => field.province === 'EMPLOYEE'
          );

    return fields.sort((a, b) => (a.ordinal || 0) - (b.ordinal || 0));
  }

  /**
   * Get custom fields for PROGRAM province from program instance
   * In edit mode, uses tracked copy; otherwise uses live model data
   * Sorted by ordinal for consistent display
   */
  get customProgramFields(): CustomFieldSignature[] {
    const fields: CustomFieldSignature[] =
      this.args.isEditingApplicationDetails && this.programInstanceCustomFieldsCopy
        ? this.programInstanceCustomFieldsCopy.filter((field) => field.province === 'PROGRAM')
        : (this.args.model.tasProgramInstance?.customFields || []).filter(
            (field) => field.province === 'PROGRAM'
          );

    return fields.sort((a, b) => (a.ordinal || 0) - (b.ordinal || 0));
  }

  /**
   * Get custom fields for APPLICATION province from application
   * In edit mode, uses tracked copy; otherwise uses live model data
   * Sorted by ordinal for consistent display
   */
  get customApplicationFields(): CustomFieldSignature[] {
    const fields: CustomFieldSignature[] =
      this.args.isEditingApplicationDetails && this.applicationCustomFieldsCopy
        ? this.applicationCustomFieldsCopy.filter((field) => field.province === 'APPLICATION')
        : (this.args.model?.customFields || []).filter((field) => field.province === 'APPLICATION');

    return fields.sort((a, b) => (a.ordinal || 0) - (b.ordinal || 0));
  }

  /**
   * Unified field lookup that searches fields and customFields in priority order
   * @param fieldName - The name of the field to retrieve
   * @returns The field value or 'N/A' if not found
   */
  private getFieldValueFromAnyTasField(fieldName: string): string | number | boolean | null {
    // Helper to check if a value is valid (not undefined, null, or empty string)
    const isValidValue = (value: unknown): value is string | number | boolean => {
      return value !== undefined && value !== null && value !== '';
    };

    // Try application fields first
    const appFields = this.args.model.fields as Record<string, { values: unknown[] }> | undefined;
    const appFieldValue = appFields?.[fieldName]?.values?.[0];
    if (isValidValue(appFieldValue)) {
      return appFieldValue;
    }

    // Try application custom fields
    const appCustomField = this.args.model.customFields?.find((f) => f.name === fieldName);
    const appCustomFieldValue = appCustomField?.values?.[0];
    if (isValidValue(appCustomFieldValue)) {
      return appCustomFieldValue;
    }

    // Try program instance fields
    const programInstance = this.args.model.tasProgramInstance;
    const piFields = programInstance?.fields as Record<string, { values: unknown[] }> | undefined;
    const piFieldValue = piFields?.[fieldName]?.values?.[0];
    if (isValidValue(piFieldValue)) {
      return piFieldValue;
    }

    // Try program instance custom fields
    const piCustomField = programInstance?.customFields?.find((f) => f.name === fieldName);
    const piCustomFieldValue = piCustomField?.values?.[0];
    if (isValidValue(piCustomFieldValue)) {
      return piCustomFieldValue;
    }

    return 'N/A';
  }

  get applicationDetailsRows() {
    const employee = this.args.model.tasProgramInstance?.employee;
    const programTemplate = this.args.model.tasProgramInstance?.tasProgramTemplate;
    const templateFields = programTemplate?.fields as ProgramTemplateFields;
    const employmentStartDate =
      employee?.tasParticipant?.employmentStartDate || employee?.employmentStartDate;
    const applicationSubmittedDate = this.args.model.applicationSubmittedDate;

    // Balance multipliers (parity with the classic approval page's Employee
    // Information section). Both are shown only when applicable — i.e. when a
    // truthy multiplier is set — matching the old page's `{{#if}}` rendering.
    const currentBalanceMultiplier =
      Number(employee?.customAttributes.raw?.tmBalanceMultiplier) * 100;
    const applicationBalanceMultiplier =
      Number(this.args.model.tasProgramLimit?.limitCoefficient) * 100;

    // Helper function to format date - uses UTC to avoid timezone shifting
    const formatDate = (dateStr: string) => {
      return formatISODateToDisplay(dateStr, this.intl, '2-digit');
    };

    // TasParticipant stores this in a `date` column, Employee in a `datetime` one, so
    // the latter's leading YYYY-MM-DD is the previous day west of UTC. The instant's
    // UTC calendar date is the stored one.
    const formatCalendarDate = (dateStr: string) => {
      if (!dateStr || dateStr === 'N/A') return 'N/A';
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        return formatISODateToDisplay(dateStr, this.intl, '2-digit');
      }

      const instant = new Date(dateStr);
      if (Number.isNaN(instant.getTime())) {
        return formatISODateToDisplay(dateStr, this.intl, '2-digit');
      }

      return this.intl.formatDate(instant, {
        month: '2-digit',
        day: '2-digit',
        year: '2-digit',
        timeZone: 'UTC',
      });
    };

    // `createdAt` and `applicationSubmittedDate` are full instants, not the
    // date-only strings `formatDate` handles: it slices the leading YYYY-MM-DD
    // off and pins that to UTC, so an 8pm-Eastern submit renders as the next
    // day. Render the instant in the viewer's zone, labelled, so it can't be
    // silently compared against the bare COURSES_* dates below.
    const formatTimestamp = (value: string | undefined) => {
      if (!value) return 'N/A';

      try {
        // Two calls joined by hand: one combined date+time format emits a
        // locale comma, and the timeline separates the parts with a pipe.
        const date = this.intl.formatDate(value, {
          month: '2-digit',
          day: '2-digit',
          year: '2-digit',
        });
        const time = this.intl.formatDate(value, {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
          timeZoneName: 'short',
        });

        return `${date} | ${time}`;
      } catch {
        return 'N/A';
      }
    };

    // Helper to check if a field is visible
    const isFieldVisible = (fieldName: string): boolean => {
      return templateFields?.[fieldName]?.visible === true;
    };

    const getFieldLabel = (fieldName: string, fallback: string): string => {
      return templateFields?.[fieldName]?.label || fallback;
    };

    const rows = [
      {
        label: 'Employment Type',
        value:
          employee?.employmentType
            ?.replace('_', ' ')
            ?.toLowerCase()
            ?.replace(/\b\w/g, (l) => l.toUpperCase()) || 'N/A',
        isSystemField: true, // Always show system fields
      },
      {
        label: 'Hire Date',
        value: formatCalendarDate(employmentStartDate || 'N/A'),
        isSystemField: true,
      },
      {
        label: this.intl.t('tuition_assistance.application_details.balance_multiplier'),
        value: `${currentBalanceMultiplier}%`,
        isSystemField: false,
        // `Boolean(n)` mirrors the classic page's `{{#if this.currentMultiplier}}`
        // truthiness exactly (0 and NaN are hidden; any other value shows).
        showWhenApplicable: Boolean(currentBalanceMultiplier),
      },
      {
        label: this.intl.t('tuition_assistance.application_details.app_balance_multiplier'),
        value: `${applicationBalanceMultiplier}%`,
        isSystemField: false,
        showWhenApplicable: Boolean(applicationBalanceMultiplier),
      },
      {
        label: 'Program',
        value: programTemplate?.programName
          ? `${programTemplate.programName} (${programTemplate.code})`
          : 'N/A',
        isSystemField: true,
        linkRoute: this.args.programTemplateRoute,
        linkModel: programTemplate?.id || undefined,
      },
      {
        label: this.intl.t('tuition_assistance.program_details.status.created'),
        value: formatTimestamp(this.args.model.createdAt),
        isSystemField: true,
      },
      {
        label: 'Submitted On',
        value: formatTimestamp(applicationSubmittedDate),
        isSystemField: true,
      },
      {
        label: getFieldLabel('COURSES_BEGIN_DATE', 'Course Starts'),
        value: formatDate(
          this.getFieldValueFromAnyTasField('COURSES_BEGIN_DATE')?.toString() || 'N/A'
        ),
        fieldName: 'COURSES_BEGIN_DATE',
        isSystemField: false,
      },
      {
        label: getFieldLabel('COURSES_END_DATE', 'Course Ends'),
        value: formatDate(
          this.getFieldValueFromAnyTasField('COURSES_END_DATE')?.toString() || 'N/A'
        ),
        fieldName: 'COURSES_END_DATE',
        isSystemField: false,
      },
      {
        label: getFieldLabel('COURSES_RECEIPT_DATE', 'Course Receipt Date'),
        value: formatDate(
          this.getFieldValueFromAnyTasField('COURSES_RECEIPT_DATE')?.toString() || 'N/A'
        ),
        fieldName: 'COURSES_RECEIPT_DATE',
        isSystemField: false,
      },
      {
        label: getFieldLabel('SEMESTER_CODE', 'Semester Code'),
        value: this.getFieldValueFromAnyTasField('SEMESTER_CODE') || 'N/A',
        fieldName: 'SEMESTER_CODE',
        isSystemField: false,
      },
      {
        label: getFieldLabel('GRADE_POINT_AVERAGE', 'Grade Point Average'),
        value: this.getFieldValueFromAnyTasField('GRADE_POINT_AVERAGE') || 'N/A',
        fieldName: 'GRADE_POINT_AVERAGE',
        isSystemField: false,
        showWhenGpaIsUsed: true,
      },
      {
        label: getFieldLabel('SCHOOL_INSTITUTION_NAME', 'Institution'),
        value: this.getFieldValueFromAnyTasField('SCHOOL_INSTITUTION_NAME'),
        fieldName: 'SCHOOL_INSTITUTION_NAME',
        isSystemField: false,
      },
      {
        label: getFieldLabel('PROGRAM_SPECIALIZATION', 'Specialization'),
        value: this.getFieldValueFromAnyTasField('PROGRAM_SPECIALIZATION'),
        fieldName: 'PROGRAM_SPECIALIZATION',
        isSystemField: false,
      },
      {
        label: getFieldLabel('PROGRAM_MAJOR', 'Major'),
        value: this.getFieldValueFromAnyTasField('PROGRAM_MAJOR'),
        fieldName: 'PROGRAM_MAJOR',
        isSystemField: false,
      },
      {
        label: getFieldLabel('ESTIMATED_PROGRAM_BEGIN', 'Program Begin Date'),
        value: formatDate(
          this.getFieldValueFromAnyTasField('ESTIMATED_PROGRAM_BEGIN')?.toString() || 'N/A'
        ),
        fieldName: 'ESTIMATED_PROGRAM_BEGIN',
        isSystemField: false,
      },
      {
        label: getFieldLabel('ESTIMATED_PROGRAM_COMPLETION', 'Program Completion Date'),
        value: formatDate(
          this.getFieldValueFromAnyTasField('ESTIMATED_PROGRAM_COMPLETION')?.toString() || 'N/A'
        ),
        fieldName: 'ESTIMATED_PROGRAM_COMPLETION',
        isSystemField: false,
      },
      {
        label: getFieldLabel('ESTIMATED_COST', 'Estimated Cost'),
        value: (() => {
          const cost = this.getFieldValueFromAnyTasField('ESTIMATED_COST');
          if (!cost || cost === 'N/A') return 'N/A';
          const numericCost = Number(cost);
          return isNaN(numericCost) ? 'N/A' : `$${(numericCost / 100).toFixed(2)}`;
        })(),
        fieldName: 'ESTIMATED_COST',
        isSystemField: false,
      },
      {
        label: getFieldLabel('ENROLLMENT_OBJECTIVE', 'Enrollment Objective'),
        value: this.getFieldValueFromAnyTasField('ENROLLMENT_OBJECTIVE') || 'N/A',
        fieldName: 'ENROLLMENT_OBJECTIVE',
        isSystemField: false,
      },
      {
        label: getFieldLabel('INVOICE_DATE', 'Invoice Date'),
        value: formatDate(this.getFieldValueFromAnyTasField('INVOICE_DATE')?.toString() || 'N/A'),
        fieldName: 'INVOICE_DATE',
        isSystemField: false,
      },
    ];

    // Filter to only show system fields and visible template fields
    return rows.filter(
      (row) =>
        row.isSystemField ||
        (row.showWhenGpaIsUsed && this.shouldShowGradePointAverageInApplicationDetails) ||
        row.showWhenApplicable ||
        (row.fieldName && isFieldVisible(row.fieldName))
    );
  }

  /**
   * Helper function to format custom field values for read-only display
   * Handles currency, date, and option-based formatting
   */
  private formatCustomFieldValue = (
    value: string | boolean | number,
    field: CustomFieldSignature
  ): string => {
    // First check if there's an option mapping (for select/radio/checkbox fields)
    const option = field.options?.find(
      (opt: CustomFieldOption) =>
        opt.value === value || String(opt.value).toLowerCase() === String(value).toLowerCase()
    );
    if (option) {
      return option.label;
    }

    const inputType = field.inputType;

    if (inputType === 'currency') {
      // Value is in cents, convert to dollars
      const numValue = Number(value);
      if (isNaN(numValue)) return String(value);
      return `$${(numValue / 100).toFixed(2)}`;
    }

    if (inputType === 'date') {
      // Value is in format "YYYY-MM-DD", convert to "MM/DD/YYYY" using UTC
      return formatISODateToDisplay(String(value), this.intl, 'numeric');
    }

    if (
      value === true ||
      value === false ||
      String(value).toLowerCase() === 'true' ||
      String(value).toLowerCase() === 'false'
    ) {
      return String(value).toLowerCase() === 'true' ? this.intl.t('yes') : this.intl.t('no');
    }

    // Default: return as string
    return String(value);
  };

  /**
   * Get formatted EMPLOYEE province custom field rows for read-only display
   */
  get customEmployeeFieldRows() {
    return this.customEmployeeFields
      .filter((field: CustomFieldSignature) => field.visible)
      .map((field: CustomFieldSignature) => {
        const values = field.values || [];
        if (values.length === 0) return null;

        // Format value(s)
        let formattedValue = '';
        if (values.length > 1) {
          formattedValue = values
            .map((value) => this.formatCustomFieldValue(value, field))
            .join(', ');
        } else if (values[0] !== undefined) {
          formattedValue = this.formatCustomFieldValue(values[0], field);
        }

        return {
          label: field.label || field.name || '',
          value: formattedValue,
        };
      })
      .filter((row: { label: string; value: string } | null) => row !== null);
  }

  /**
   * Get formatted PROGRAM province custom field rows for read-only display
   */
  get customProgramFieldRows() {
    return this.customProgramFields
      .filter((field: CustomFieldSignature) => field.visible)
      .map((field: CustomFieldSignature) => {
        const values = field.values || [];
        if (values.length === 0) return null;

        // Format value(s)
        let formattedValue = '';
        if (values.length > 1) {
          formattedValue = values
            .map((value) => this.formatCustomFieldValue(value, field))
            .join(', ');
        } else if (values[0] !== undefined) {
          formattedValue = this.formatCustomFieldValue(values[0], field);
        }

        return {
          label: field.label || field.name || '',
          value: formattedValue,
        };
      })
      .filter((row: { label: string; value: string } | null) => row !== null);
  }

  /**
   * Get formatted APPLICATION province custom field rows for read-only display
   */
  get customApplicationFieldRows() {
    return this.customApplicationFields
      .filter((field: CustomFieldSignature) => field.visible)
      .map((field: CustomFieldSignature) => {
        const values = field.values || [];
        if (values.length === 0) return null;

        // Format value(s)
        let formattedValue = '';
        if (values.length > 1) {
          formattedValue = values
            .map((value) => this.formatCustomFieldValue(value, field))
            .join(', ');
        } else if (values[0] !== undefined) {
          formattedValue = this.formatCustomFieldValue(values[0], field);
        }

        return {
          label: field.label || field.name || '',
          value: formattedValue,
        };
      })
      .filter((row: { label: string; value: string } | null) => row !== null);
  }

  /**
   * Get formatted custom application field rows for read-only display
   * @deprecated Use province-specific getters instead: customEmployeeFieldRows, customProgramFieldRows, customApplicationFieldRows
   * All custom fields on the program instance
   */
  get customApplicationDetailFieldRows() {
    const customFields = this.customApplicationDetailFields;
    if (!customFields || customFields.length === 0) return [];

    /**
     * Format a single value based on field type
     */
    const formatValue = (value: string | boolean | number, field: CustomFieldSignature): string => {
      // First check if there's an option mapping (for select/radio/checkbox fields)
      const option = field.options?.find((opt: CustomFieldOption) => opt.value === value);
      if (option) {
        return option.label;
      }

      const inputType = field.inputType;

      if (inputType === 'currency') {
        // Value is in cents, convert to dollars
        const numValue = Number(value);
        if (isNaN(numValue)) return String(value);
        return `$${(numValue / 100).toFixed(2)}`;
      }

      if (inputType === 'date') {
        // Value is in format "YYYY-MM-DD", convert to "MM/DD/YYYY" using UTC
        return formatISODateToDisplay(String(value), this.intl, 'numeric');
      }

      // Default: return as string
      return String(value);
    };

    return customFields
      .filter((field: CustomFieldSignature) => field.visible)
      .map((field: CustomFieldSignature) => {
        const values = field.values || [];
        if (values.length === 0) return null;

        // Format value(s)
        let formattedValue = '';
        if (values.length > 1) {
          formattedValue = values.map((value) => formatValue(value, field)).join(', ');
        } else if (values[0] !== undefined) {
          formattedValue = formatValue(values[0], field);
        }

        return {
          label: field.label || field.name || '',
          value: formattedValue,
        };
      })
      .filter((row: { label: string; value: string } | null) => row !== null);
  }

  /**
   * Check if user can edit application details.
   * Editing is not allowed once an application is fulfilled and payment/use has occurred:
   * - Reimbursement apps: FULFILLED + any paid amount recorded (`amounts.paid > 0`)
   * - CourseCash (card) apps: FULFILLED + card amounts have been used (`amounts.course_cash.used > 0`)
   */
  get canEditApplicationDetails() {
    if (this.args.isReadOnly) {
      return false;
    }

    if (!(this.sessionContext.canEditTasApplication ?? false)) {
      return false;
    }

    const app = this.args.model;
    if (app.state !== 'TAS.ApplicationState.FULFILLED') {
      return true;
    }

    const isCourseCash = app.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH';
    if (isCourseCash) {
      // For card-based apps, "paid" means card funds were spent — no reimbursement paymentState
      const courseCashUsed = (app.amounts?.course_cash?.used ?? 0) > 0;
      return !courseCashUsed;
    }

    // Any payment at all (partial or full) means editing is locked
    const hasBeenPaid = (app.amounts?.paid ?? 0) > 0;
    return !hasBeenPaid;
  }

  /**
   * Determine which model a field belongs to
   */
  getFieldSource(fieldName: string): 'application' | 'programInstance' {
    if (fieldName === 'GRADE_POINT_AVERAGE') {
      return 'application';
    }

    const appFields = APPLICATION_FIELD_CONFIGS.map((c) => c.name);
    return appFields.includes(fieldName as (typeof appFields)[number])
      ? 'application'
      : 'programInstance';
  }

  /**
   * Handle updates to APPLICATION province custom fields
   * Updates only the tracked copy during edit mode
   */
  didUpdateApplicationCustomFields = (customFields: CustomFieldSignature[]) => {
    if (!this.applicationCustomFieldsCopy) return;

    const updatedFields = this.args.updateFieldsWithNewValues(
      this.applicationCustomFieldsCopy,
      customFields
    );

    this.applicationCustomFieldsCopy = updatedFields;
  };

  /**
   * Handle updates to EMPLOYEE province custom fields
   * Updates only the tracked copy during edit mode
   */
  didUpdateEmployeeCustomFields = (customFields: CustomFieldSignature[]) => {
    if (!this.programInstanceCustomFieldsCopy) return;

    const updatedFields = this.args.updateFieldsWithNewValues(
      this.programInstanceCustomFieldsCopy,
      customFields
    );

    this.programInstanceCustomFieldsCopy = updatedFields;
  };

  /**
   * Handle updates to PROGRAM province custom fields
   * Updates only the tracked copy during edit mode
   */
  didUpdateProgramCustomFields = (customFields: CustomFieldSignature[]) => {
    if (!this.programInstanceCustomFieldsCopy) return;

    const updatedFields = this.args.updateFieldsWithNewValues(
      this.programInstanceCustomFieldsCopy,
      customFields
    );

    this.programInstanceCustomFieldsCopy = updatedFields;
  };

  /**
   * Toggle application details edit mode
   */
  toggleApplicationDetailsEdit = async () => {
    if (this.args.isEditingApplicationDetails) {
      // Cancel - discard changes
      this.args.onEditingApplicationDetailsChange(false);
      this.args.onApplicationFieldsCopyChange(null);
      this.applicationCustomFieldsCopy = null;
      this.programInstanceFieldsCopy = null;
      this.programInstanceCustomFieldsCopy = null;
      this.applicationDetailsChangelog = '';
      this.selectedSchool = undefined;
      this.hasSubmittedApplicationDetails = false;
      this.courseDateValidationErrors = {};
    } else {
      // Enter edit mode - create tracked copies
      const programInstance = this.args.model.tasProgramInstance;
      if (!programInstance) {
        console.error('No program instance found');
        return;
      }

      this.args.onApplicationFieldsCopyChange(trackedObject(this.args.model.fields));
      this.programInstanceFieldsCopy = trackedObject(programInstance.fields);

      // Create deep copies of custom fields arrays to track changes
      this.applicationCustomFieldsCopy = this.args.model.customFields
        ? JSON.parse(JSON.stringify(this.args.model.customFields))
        : [];
      this.programInstanceCustomFieldsCopy = programInstance.customFields
        ? JSON.parse(JSON.stringify(programInstance.customFields))
        : [];

      this.applicationDetailsChangelog = '';
      this.hasSubmittedApplicationDetails = false;

      // Load previously selected school if available
      try {
        const tasInstitutionId = programInstance.fields['TAS_INSTITUTION_ID']?.values?.[0];
        const dapipInstitutionId = programInstance.fields['DAPIP_INSTITUTION_ID']?.values?.[0];

        if (tasInstitutionId || dapipInstitutionId) {
          const queryParams: Record<string, string | number> = {
            'filter[tas-program-template]': programInstance.tasProgramTemplate.id!,
            'page[limit]': 1,
            'page[offset]': 0,
          };

          if (dapipInstitutionId) {
            queryParams['filter[dapip-id]'] = dapipInstitutionId;
          }

          if (tasInstitutionId) {
            queryParams['filter[institution-id]'] = tasInstitutionId;
          }

          // WarpDrive builder read (Phase 2.4) — replaces store.query<T>();
          // flattened bracket keys satisfy the builder's QueryParamsSource type.
          const { content } = (await this.store.request(
            query<TasUnifiedInstitutionModel>('tas-unified-institution', queryParams)
          )) as { content: { data: TasUnifiedInstitutionModel[] } };

          if (content.data.length > 0) {
            this.selectedSchool = content.data[0];
          }
        }
      } catch (e) {
        console.error('Error attempting to load previously selected school', e);
      }

      this.args.onEditingApplicationDetailsChange(true);

      // Run initial validation for existing date values
      this.validateCourseDates();
    }
  };

  /**
   * Type guards for TasUnifiedInstitution properties
   */
  private hasValidLocationName(
    institution: TasUnifiedInstitution | undefined
  ): institution is TasUnifiedInstitution & { locationName: string } {
    return !!(institution && 'locationName' in institution && institution.locationName);
  }

  private hasValidInstitutionId(
    institution: TasUnifiedInstitution | undefined
  ): institution is TasUnifiedInstitution & { institutionId: string } {
    return !!(institution && 'institutionId' in institution && institution.institutionId);
  }

  private hasValidDapipId(
    institution: TasUnifiedInstitution | undefined
  ): institution is TasUnifiedInstitution & { dapipId: string } {
    return !!(institution && 'dapipId' in institution && institution.dapipId);
  }

  /**
   * Handle school selection from SchoolSelect component
   */
  didSelectSchool = (searchResult?: TasUnifiedInstitution) => {
    this.selectedSchool = searchResult as unknown as TasUnifiedInstitutionModel;

    if (!searchResult || !this.programInstanceFieldsCopy) {
      return;
    }

    setSingleValueForTasField(
      'SCHOOL_INSTITUTION_NAME',
      this.hasValidLocationName(searchResult) ? searchResult.locationName : '',
      this.programInstanceFieldsCopy
    );

    setSingleValueForTasField(
      'TAS_INSTITUTION_ID',
      this.hasValidInstitutionId(searchResult) ? searchResult.institutionId : '',
      this.programInstanceFieldsCopy
    );

    setSingleValueForTasField(
      'DAPIP_INSTITUTION_ID',
      this.hasValidDapipId(searchResult) ? searchResult.dapipId : '',
      this.programInstanceFieldsCopy
    );
  };

  /**
   * Update application detail field value
   */
  updateApplicationDetailField = (
    fieldName: string,
    value?: string | string[] | number,
    event?: Event
  ) => {
    const source = this.getFieldSource(fieldName);
    const fieldsCopy =
      source === 'application' ? this.args.applicationFieldsCopy : this.programInstanceFieldsCopy;

    if (!fieldsCopy) return;

    let valueToSet: string | number | null = Array.isArray(value)
      ? (value[0] ?? '')
      : (value ?? '');

    if (fieldName === 'GRADE_POINT_AVERAGE' && valueToSet === '') {
      valueToSet = null;
    }

    const target = event?.target as InputmaskTarget | null;
    if (target?.inputmask?.userOptions?.alias === 'currency') {
      valueToSet = Number(target.inputmask.unmaskedvalue()) * 100;
    }

    // Use type assertion - TypeScript can't infer the relationship between fieldName and fieldsCopy
    if (source === 'application') {
      setSingleValueForTasField(
        fieldName as keyof TasApplicationModel['fields'],
        valueToSet,
        fieldsCopy as TasApplicationModel['fields']
      );
    } else {
      setSingleValueForTasField(
        fieldName as keyof TasProgramInstanceModel['fields'],
        valueToSet,
        fieldsCopy as TasProgramInstanceModel['fields']
      );
    }
  };

  /**
   * Wrapper for updating date fields from EmberFlatpickr
   * Converts Date[] to ISO string and calls updateApplicationDetailField
   * Also triggers validation and updates SEMESTER_CODE for begin date
   */
  updateDateField = (fieldName: string, selectedDates: Date[]) => {
    if (selectedDates.length > 0 && selectedDates[0]) {
      const isoDate = formatDateToISO(selectedDates[0]);
      this.updateApplicationDetailField(fieldName, isoDate);

      // Update SEMESTER_CODE if begin date changed and field is visible
      if (fieldName === 'COURSES_BEGIN_DATE') {
        const template = this.args.model.tasProgramInstance?.tasProgramTemplate;
        if (template?.semesterCodeIsVisible) {
          const semesterCode = setSemesterCodeString(isoDate);
          this.updateApplicationDetailField('SEMESTER_CODE', semesterCode);
        }
      }

      // Trigger validation after date change
      this.validateCourseDates();
    }
  };

  /**
   * Get application detail field value for editing
   */
  getApplicationDetailValue = (fieldName: string): string => {
    const source = this.getFieldSource(fieldName);
    const fieldsCopy =
      source === 'application' ? this.args.applicationFieldsCopy : this.programInstanceFieldsCopy;

    if (!fieldsCopy) return '';

    let value: string | number | boolean | null;
    if (source === 'application') {
      value = getSingleValueForTasField(
        fieldName as keyof TasApplicationModel['fields'],
        fieldsCopy as TasApplicationModel['fields']
      );
    } else {
      value = getSingleValueForTasField(
        fieldName as keyof TasProgramInstanceModel['fields'],
        fieldsCopy as TasProgramInstanceModel['fields']
      );
    }

    // Handle currency fields - convert cents to dollars for display
    if (fieldName === 'ESTIMATED_COST' && value) {
      return String(Number(value) / 100);
    }

    return value ? String(value) : '';
  };

  get shouldShowGradePointAverageInApplicationDetails() {
    return Boolean(this.args.model.tasProgramInstance?.tasProgramTemplate?.calculateTotalByGPA);
  }

  get gradePointAverageApplicationDetailsLabel() {
    return (
      this.args.model.tasProgramInstance?.tasProgramTemplate?.fields?.GRADE_POINT_AVERAGE?.label ||
      'Grade Point Average'
    );
  }

  get gradePointAverageApplicationDetailsValidationErrors() {
    const value = this.getApplicationDetailValue('GRADE_POINT_AVERAGE').trim();
    if (!value) {
      return [];
    }

    const gradePointAverage = Number(value);
    if (!Number.isFinite(gradePointAverage)) {
      return ['GPA must be a number'];
    }

    if (gradePointAverage < 0 || gradePointAverage > 4) {
      return ['GPA must be between 0 and 4.0'];
    }

    return [];
  }

  applyGradePointAverageApprovedTotal = () => {
    if (!this.shouldShowGradePointAverageInApplicationDetails || !this.args.applicationFieldsCopy) {
      return;
    }

    if (this.gradePointAverageApplicationDetailsValidationErrors.length) {
      return;
    }

    const gradePointAverage = this.getApplicationDetailValue('GRADE_POINT_AVERAGE');
    if (!gradePointAverage) {
      setSingleValueForTasField('FINAL_APPROVED_TOTAL', 0, this.args.applicationFieldsCopy);
      this.args.onApplicationTotalChange(0);
      return;
    }

    const gpaBasedAmount = this.tuitionAssistance.setTotalBasedOnGPA(Number(gradePointAverage));
    const approvedTotal = Math.min(gpaBasedAmount, this.args.model.requestedTotal);

    setSingleValueForTasField(
      'FINAL_APPROVED_TOTAL',
      approvedTotal,
      this.args.applicationFieldsCopy
    );
    this.args.onApplicationTotalChange(approvedTotal);
  };

  /**
   * Update custom program field value
   * Updates the tracked copy of custom fields for reactive editing
   */
  updateCustomProgramField = (fieldName: string, updatedField: CustomFieldSignature) => {
    if (!this.programInstanceCustomFieldsCopy) return;

    const customFieldIndex = this.programInstanceCustomFieldsCopy.findIndex(
      (f) => f.name === fieldName
    );
    if (customFieldIndex >= 0) {
      this.programInstanceCustomFieldsCopy[customFieldIndex] = updatedField;
    }
  };

  /**
   * Update application details changelog
   */
  updateApplicationDetailsChangelog = (value: string) => {
    this.applicationDetailsChangelog = value;
  };

  /**
   * Validate application details changelog
   */
  get applicationDetailsChangelogValidationErrors(): string[] {
    const schema = v.pipe(v.string(), v.minLength(1, 'Comment is required'));
    const result = v.safeParse(schema, this.applicationDetailsChangelog);
    return result.success ? [] : result.issues.map((issue) => issue.message);
  }

  /**
   * Get flattened errors compatible with errorsForValibotField helper
   */
  get courseDateFormValidationErrors(): Record<string, readonly string[] | undefined> {
    if (!this.args.isEditingApplicationDetails) {
      return {};
    }

    const beginDateErrors = this.courseDateValidationErrors['COURSES_BEGIN_DATE'];
    const endDateErrors = this.courseDateValidationErrors['COURSES_END_DATE'];

    return {
      COURSES_BEGIN_DATE: beginDateErrors?.length ? beginDateErrors : undefined,
      COURSES_END_DATE: endDateErrors?.length ? endDateErrors : undefined,
    };
  }

  /**
   * Get field config from template for date validation
   */
  getDateFieldConfig = (fieldName: 'COURSES_BEGIN_DATE' | 'COURSES_END_DATE'): FieldConfig => {
    const template = this.args.model.tasProgramInstance?.tasProgramTemplate;
    const fieldConfig = template?.fields?.[fieldName];

    return {
      visible: fieldConfig?.visible ?? false,
      required: fieldConfig?.required ?? false,
    };
  };

  /**
   * Validate course dates
   * Runs validation for both COURSES_BEGIN_DATE and COURSES_END_DATE
   */
  validateCourseDates = () => {
    if (!this.args.isEditingApplicationDetails) {
      this.courseDateValidationErrors = {};
      return;
    }

    const template = this.args.model.tasProgramInstance?.tasProgramTemplate;
    if (!template) {
      this.courseDateValidationErrors = {};
      return;
    }

    const beginDate = this.getApplicationDetailValue('COURSES_BEGIN_DATE');
    const endDate = this.getApplicationDetailValue('COURSES_END_DATE');

    const beginFieldConfig = this.getDateFieldConfig('COURSES_BEGIN_DATE');
    const endFieldConfig = this.getDateFieldConfig('COURSES_END_DATE');

    // Get employment start date from tasParticipant (not HIRE_DATE field)
    const employmentStartDate =
      this.args.model.tasProgramInstance?.employee?.tasParticipant?.employmentStartDate;
    const eligibilityWaitingPeriod = template.eligibilityWaitingPeriod;

    this.courseDateValidationErrors = {
      COURSES_BEGIN_DATE: validateBeginDate(
        beginDate,
        beginFieldConfig,
        template,
        this.intl,
        employmentStartDate,
        eligibilityWaitingPeriod
      ),
      COURSES_END_DATE: validateEndDate(
        endDate,
        beginDate,
        endFieldConfig,
        template.isCertificateProgram ?? false
      ),
    };
  };

  /**
   * Check if application details form is valid
   * Validates changelog, required standard fields, and all custom fields
   * Note: Date validation errors are shown as warnings but don't block submission
   */
  get isApplicationDetailsFormValid(): boolean {
    // Check changelog validation
    if (this.applicationDetailsChangelogValidationErrors.length > 0) {
      return false;
    }

    // Date validation errors are displayed but don't block submission
    // Only missing required fields and missing changelog should prevent submission

    // Check required standard TAS fields (from program template)
    const template = this.args.model.tasProgramInstance?.tasProgramTemplate;
    const templateFields = template?.fields as ProgramTemplateFields | undefined;

    if (templateFields) {
      for (const fieldConfig of APPLICATION_FIELD_CONFIGS) {
        const fieldName = fieldConfig.name;
        const fieldMeta = templateFields[fieldName];

        if (fieldMeta?.required) {
          const value = this.getApplicationDetailValue(fieldName);
          if (!value || value === '') {
            return false;
          }
        }
      }

      for (const fieldConfig of PROGRAM_INSTANCE_FIELD_CONFIGS) {
        const fieldName = fieldConfig.name;
        const fieldMeta = templateFields[fieldName];

        if (fieldMeta?.required) {
          const value = this.getApplicationDetailValue(fieldName);
          if (!value || value === '') {
            return false;
          }
        }
      }
    }

    if (this.gradePointAverageApplicationDetailsValidationErrors.length > 0) {
      return false;
    }

    // Check all custom fields for validation errors
    const allFields = [
      ...(this.programInstanceCustomFieldsCopy || []),
      ...(this.applicationCustomFieldsCopy || []),
    ];

    for (const field of allFields) {
      const errors = this.getCustomFieldValidationErrors(field.name || '');
      if (errors.length > 0) {
        return false;
      }
    }

    return true;
  }

  /**
   * Get validation errors for a specific custom field
   * Currently validates only required fields
   * @param fieldName - The name of the custom field
   * @returns Array of error messages for this field
   */
  getCustomFieldValidationErrors = (fieldName: string): string[] => {
    const allFields = [
      ...(this.programInstanceCustomFieldsCopy || []),
      ...(this.applicationCustomFieldsCopy || []),
    ];

    const field = allFields.find((f) => f.name === fieldName);
    if (!field) return [];

    const errors: string[] = [];

    // Check required field validation
    if (field.required) {
      const hasValue =
        field.values &&
        field.values.length > 0 &&
        field.values[0] !== null &&
        field.values[0] !== undefined &&
        field.values[0] !== '';

      if (!hasValue) {
        errors.push(`${field.label || fieldName} is required`);
      }
    }

    // Future: Add more validation rules here (min/max length, patterns, etc.)

    return errors;
  };

  /**
   * Get validation errors for all custom fields across all provinces
   * Returns array in format expected by TuitionAssistanceFormsCustomFieldsForProvince
   * Uses existing getCustomFieldValidationErrors method for actual validation
   */
  get customFieldValidationErrors(): ValidationErrorSignature[] {
    // Only validate during edit mode
    if (!this.args.isEditingApplicationDetails) {
      return [];
    }

    const allFields = [
      ...this.customEmployeeFields,
      ...this.customProgramFields,
      ...this.customApplicationFields,
    ];

    const errors: ValidationErrorSignature[] = [];

    allFields.forEach((field) => {
      const fieldErrors = this.getCustomFieldValidationErrors(field.name || '');
      if (fieldErrors.length > 0) {
        errors.push({
          path: field.label || field.name || '',
          value: field.values?.[0]?.toString() || '',
          errors: fieldErrors,
        });
      }
    });

    return errors;
  }

  /**
   * Get custom application fields from both program instance and application
   * Uses tracked copies when in edit mode, otherwise uses live model data
   */
  get customApplicationDetailFields() {
    // When editing, use tracked copies; otherwise use live model data
    if (this.args.isEditingApplicationDetails) {
      const programInstanceFields = this.programInstanceCustomFieldsCopy || [];
      const applicationFields = this.applicationCustomFieldsCopy || [];

      return [...programInstanceFields, ...applicationFields].sort(
        (a, b) => (a.ordinal || 0) - (b.ordinal || 0)
      );
    }

    const programInstance = this.args.model.tasProgramInstance;
    const application = this.args.model;

    // Combine custom fields from both program instance and application
    const programInstanceFields = programInstance?.customFields || [];
    const applicationFields = application?.customFields || [];

    return [...programInstanceFields, ...applicationFields].sort(
      (a, b) => (a.ordinal || 0) - (b.ordinal || 0)
    );
  }

  isSchoolSelectProgramInstanceField = (fieldName: TemplateFieldName) => {
    const programTemplate = this.args.model.tasProgramInstance?.tasProgramTemplate;
    if (!programTemplate?.fields) {
      return fieldName === 'SCHOOL_INSTITUTION_NAME';
    }

    return isTasFieldComponentType(fieldName, programTemplate.fields, 'school-select');
  };

  /**
   * Get visible program instance field configs
   * Filters PROGRAM_INSTANCE_FIELD_CONFIGS to only include fields marked visible in program template
   */
  get visibleProgramInstanceFieldConfigs() {
    const programTemplate = this.args.model.tasProgramInstance?.tasProgramTemplate;
    if (!programTemplate) return [];

    const templateFields = programTemplate.fields as ProgramTemplateFields;

    return PROGRAM_INSTANCE_FIELD_CONFIGS.filter((config) => {
      const fieldConfig = templateFields?.[config.name];
      return fieldConfig?.visible === true;
    });
  }

  /**
   * Get visible application field configs
   * Filters APPLICATION_FIELD_CONFIGS to only include fields marked visible in program template
   */
  get visibleApplicationFieldConfigs() {
    const programTemplate = this.args.model.tasProgramInstance?.tasProgramTemplate;
    if (!programTemplate) return [];

    const templateFields = programTemplate.fields as ProgramTemplateFields;

    return APPLICATION_FIELD_CONFIGS.filter((config) => {
      const fieldConfig = templateFields?.[config.name];
      return fieldConfig?.visible === true;
    });
  }

  /**
   * Save application details changes
   */
  saveApplicationDetails = dropTask(async () => {
    // Set submission flag to show validation errors
    this.hasSubmittedApplicationDetails = true;

    // Run validation before checking validity
    this.validateCourseDates();

    if (!this.isApplicationDetailsFormValid) {
      return;
    }

    const programInstance = this.args.model.tasProgramInstance;
    if (!programInstance) {
      console.error('No program instance found');
      return;
    }

    try {
      this.applyGradePointAverageApprovedTotal();

      if (this.args.applicationFieldsCopy) {
        this.args.model.fields = this.args.applicationFieldsCopy;
      }
      if (this.applicationCustomFieldsCopy) {
        this.args.model.customFields = this.applicationCustomFieldsCopy;
      }
      if (this.programInstanceFieldsCopy) {
        programInstance.fields = this.programInstanceFieldsCopy;
      }
      if (this.programInstanceCustomFieldsCopy) {
        programInstance.customFields = this.programInstanceCustomFieldsCopy;
      }

      // Save both models with changelog.
      // WarpDrive mutation builder (Phase 2.7) — replaces
      // record.save({ adapterOptions: { changelog } }); the changelog rides as
      // data.attributes._changelog on each PATCH (ApplicationSerializer parity).
      const changelog = this.applicationDetailsChangelog;

      // Save program instance if it has changes
      if (this.programInstanceFieldsCopy || this.programInstanceCustomFieldsCopy) {
        await saveExistingRecordWithChangelog(this.store, programInstance, changelog);
      }

      // Save application if it has changes
      if (this.args.applicationFieldsCopy || this.applicationCustomFieldsCopy) {
        await saveExistingRecordWithChangelog(this.store, this.args.model, changelog);
      }

      this.tuitionAssistance.refreshActiveLimitsSummaryComponents();

      // Exit edit mode and clear all tracked copies
      this.args.onEditingApplicationDetailsChange(false);
      this.args.onApplicationFieldsCopyChange(null);
      this.applicationCustomFieldsCopy = null;
      this.programInstanceFieldsCopy = null;
      this.programInstanceCustomFieldsCopy = null;
      this.applicationDetailsChangelog = '';
      this.selectedSchool = undefined;
      this.hasSubmittedApplicationDetails = false;
      this.courseDateValidationErrors = {};
    } catch (error) {
      console.error('Error saving application details:', error);
      // Don't reset hasSubmittedApplicationDetails on error so errors stay visible
    }
  });

  <template>
    {{! Application Details Section }}
    <Section @headingLevel={{3}} @headerClass="tio-text-base-semibold tio-fg-primary">
      <:header>
        <div
          class="tio-inset-summary-row tio-bg-sunken-translucent border tio-border-default-translucent max-w-3xl"
        >
          {{t "tuition_assistance.program_details.application_details"}}
          {{#if this.canEditApplicationDetails}}
            <Button
              @appearance="minimal"
              @size="sm"
              @icon={{if @isEditingApplicationDetails PhX PhPencil}}
              {{on "click" this.toggleApplicationDetailsEdit}}
              data-test-edit-application-details-button
              class="p-1.5!"
            />
          {{/if}}
        </div>
      </:header>
      <:body>
        {{#if @isEditingApplicationDetails}}
          {{! Edit Mode - Show form inputs }}
          <div class="tio-col-loose p-4 max-w-3xl">
            {{! Program Instance Fields }}
            {{#each this.visibleProgramInstanceFieldConfigs as |fieldConfig|}}
              {{#if (this.isSchoolSelectProgramInstanceField fieldConfig.name)}}
                {{! Special handling for school select }}
                <SchoolSelect
                  @programTemplate={{@model.tasProgramInstance.tasProgramTemplate}}
                  @selected={{this.selectedSchool}}
                  @onChange={{this.didSelectSchool}}
                  data-test={{fieldConfig.testId}}
                />
              {{else}}
                <TuitionAssistanceProgramFormField
                  @name={{fieldConfig.name}}
                  @programTemplate={{@model.tasProgramInstance.tasProgramTemplate}}
                  as |field|
                >
                  {{#if field.isFieldOptionPicklist}}
                    <ReSelect
                      data-test={{fieldConfig.testId}}
                      @items={{field.fieldOptions}}
                      @selected={{this.getApplicationDetailValue fieldConfig.name}}
                      @onChange={{fn this.updateApplicationDetailField fieldConfig.name}}
                      @size="sm"
                    />
                  {{else if (eq fieldConfig.type "currency")}}
                    <Input
                      data-test={{fieldConfig.testId}}
                      id={{field.inputId}}
                      @value={{this.getApplicationDetailValue fieldConfig.name}}
                      @onChange={{fn this.updateApplicationDetailField fieldConfig.name}}
                      @size="sm"
                      {{inputmask alias="currency" prefix="$" unmaskAsNumber=false digits="2"}}
                    />
                  {{else if (eq fieldConfig.type "date")}}
                    {{#let
                      (errorsForValibotField
                        fieldConfig.name
                        schemaErrors=this.courseDateFormValidationErrors
                        hasSubmitted=true
                      )
                      as |fieldErrors|
                    }}
                      <div class="space-y-1">
                        <div class="relative w-full">
                          <Flatpickr
                            @date={{or (this.getApplicationDetailValue fieldConfig.name) ""}}
                            @onChange={{fn this.updateDateField fieldConfig.name}}
                            @dateFormat="Y-m-d"
                            @altInput={{true}}
                            @altFormat="F j, Y"
                            @disableMobile={{true}}
                            data-test={{fieldConfig.testId}}
                            placeholder="mm/dd/yyyy"
                            id={{field.inputId}}
                            class="tio-form-input
                              {{if fieldErrors 'tio-border-danger' 'tio-border-default'}}"
                          />
                          <div class="tio-calendar-adornment">
                            <PhCalendarBlank @size="20" />
                          </div>
                        </div>
                        {{! Display validation errors }}
                        {{#each fieldErrors as |error|}}
                          <p class="text-sm tio-fg-danger">{{error}}</p>
                        {{/each}}
                      </div>
                    {{/let}}
                  {{else}}
                    <Input
                      data-test={{fieldConfig.testId}}
                      id={{field.inputId}}
                      @value={{this.getApplicationDetailValue fieldConfig.name}}
                      @onChange={{fn this.updateApplicationDetailField fieldConfig.name}}
                      @size="sm"
                    />
                  {{/if}}
                </TuitionAssistanceProgramFormField>
              {{/if}}
            {{/each}}

            {{! Application Fields }}
            {{#each this.visibleApplicationFieldConfigs as |fieldConfig|}}
              <TuitionAssistanceProgramFormField
                @name={{fieldConfig.name}}
                @programTemplate={{@model.tasProgramInstance.tasProgramTemplate}}
                as |field|
              >
                {{#if field.isFieldOptionPicklist}}
                  <ReSelect
                    data-test={{fieldConfig.testId}}
                    @items={{field.fieldOptions}}
                    @selected={{this.getApplicationDetailValue fieldConfig.name}}
                    @onChange={{fn this.updateApplicationDetailField fieldConfig.name}}
                    @size="sm"
                  />
                {{else if (eq fieldConfig.type "date")}}
                  {{#let
                    (errorsForValibotField
                      fieldConfig.name
                      schemaErrors=this.courseDateFormValidationErrors
                      hasSubmitted=true
                    )
                    as |fieldErrors|
                  }}
                    <div class="space-y-1">
                      <div class="relative w-full">
                        <Flatpickr
                          @date={{or (this.getApplicationDetailValue fieldConfig.name) ""}}
                          @onChange={{fn this.updateDateField fieldConfig.name}}
                          @dateFormat="Y-m-d"
                          @altInput={{true}}
                          @altFormat="F j, Y"
                          @disableMobile={{true}}
                          data-test={{fieldConfig.testId}}
                          id={{field.inputId}}
                          placeholder="mm/dd/yyyy"
                          class="tio-form-input
                            {{if fieldErrors 'tio-border-danger' 'tio-border-default'}}"
                        />
                        <div class="tio-calendar-adornment">
                          <PhCalendarBlank @size="20" />
                        </div>
                      </div>
                      {{! Display validation errors }}
                      {{#each fieldErrors as |error|}}
                        <p class="text-sm tio-fg-danger">{{error}}</p>
                      {{/each}}
                    </div>
                  {{/let}}
                {{else}}
                  <Input
                    data-test={{fieldConfig.testId}}
                    id={{field.inputId}}
                    @value={{this.getApplicationDetailValue fieldConfig.name}}
                    @onChange={{fn this.updateApplicationDetailField fieldConfig.name}}
                    @size="sm"
                  />
                {{/if}}
              </TuitionAssistanceProgramFormField>
            {{/each}}

            {{#if this.shouldShowGradePointAverageInApplicationDetails}}
              <div>
                <label
                  for="application-details-grade-point-average"
                  class="tio-text-sm-semibold tio-fg-secondary block mb-1 text-balance"
                >
                  {{this.gradePointAverageApplicationDetailsLabel}}
                </label>
                <Input
                  data-test-application-details-grade-point-average
                  id="application-details-grade-point-average"
                  @type="number"
                  @value={{this.getApplicationDetailValue "GRADE_POINT_AVERAGE"}}
                  @onChange={{fn this.updateApplicationDetailField "GRADE_POINT_AVERAGE"}}
                  @errors={{this.gradePointAverageApplicationDetailsValidationErrors}}
                  @size="sm"
                  min="0"
                  max="4.0"
                  step="0.01"
                />
              </div>
            {{/if}}

            {{! Custom Fields - Edit Mode (Province-Based) }}
            <TuitionAssistanceFormsCustomFieldsForProvince
              @province="EMPLOYEE"
              @customFields={{this.customEmployeeFields}}
              @didUpdateFields={{this.didUpdateEmployeeCustomFields}}
              @formValidationErrors={{this.customFieldValidationErrors}}
              @programTemplate={{@model.tasProgramInstance.tasProgramTemplate}}
              @fieldContainerClass="p-2"
            />

            <TuitionAssistanceFormsCustomFieldsForProvince
              @province="PROGRAM"
              @customFields={{this.customProgramFields}}
              @didUpdateFields={{this.didUpdateProgramCustomFields}}
              @formValidationErrors={{this.customFieldValidationErrors}}
              @programTemplate={{@model.tasProgramInstance.tasProgramTemplate}}
              @fieldContainerClass="p-2"
            />

            <TuitionAssistanceFormsCustomFieldsForProvince
              @province="APPLICATION"
              @customFields={{this.customApplicationFields}}
              @didUpdateFields={{this.didUpdateApplicationCustomFields}}
              @formValidationErrors={{this.customFieldValidationErrors}}
              @programTemplate={{@model.tasProgramInstance.tasProgramTemplate}}
              @fieldContainerClass="p-2"
            />

            {{! Changelog textarea }}
            <div>
              <label class="flex tio-text-sm-semibold tio-fg-secondary mb-1">
                Comment
                <span class="tio-fg-danger ml-1">*</span>
              </label>
              <Textarea
                @value={{this.applicationDetailsChangelog}}
                @onChange={{this.updateApplicationDetailsChangelog}}
                @errors={{this.applicationDetailsChangelogValidationErrors}}
                @size="sm"
                data-test-application-details-changelog
              />
            </div>

            {{! Save button }}
            <div class="tio-flex-end mt-2">
              <Button
                @intent="primary"
                @appearance="outlined"
                @size="sm"
                @isRunning={{this.saveApplicationDetails.isRunning}}
                {{on "click" this.saveApplicationDetails.perform}}
                @isDisabled={{or
                  this.saveApplicationDetails.isRunning
                  (not this.isApplicationDetailsFormValid)
                }}
                data-test-save-application-details-button
              >
                {{if this.saveApplicationDetails.isRunning "Saving..." "Save Changes"}}
              </Button>
            </div>
          </div>
        {{else}}
          {{! Read-only mode }}
          <div class="divide-y tio-divide-default max-w-3xl">
            {{#each this.applicationDetailsRows as |row|}}
              <div class="flex py-4 px-4">
                <div class="w-56 shrink-0 tio-text-sm-medium tio-fg-secondary">{{row.label}}</div>
                {{#if row.linkRoute}}
                  <LinkTo
                    @route={{row.linkRoute}}
                    @model={{row.linkModel}}
                    class="tio-text-sm-medium tio-fg-secondary px-2 hover:underline cursor-pointer"
                  >
                    {{row.value}}
                  </LinkTo>
                {{else}}
                  <ExpandableText
                    @text={{row.value}}
                    class="tio-text-sm-medium tio-fg-secondary px-2"
                  />
                {{/if}}
              </div>
            {{/each}}

            {{! Custom Application Detail Fields - Read-only (Province-Based) }}
            {{! EMPLOYEE Province }}
            {{#each this.customEmployeeFieldRows as |row|}}
              <div class="flex py-4 px-4">
                <div class="w-56 shrink-0 tio-text-sm-medium tio-fg-secondary">{{row.label}}</div>
                <ExpandableText @text={{row.value}} class="tio-text-sm-medium tio-fg-secondary" />
              </div>
            {{/each}}

            {{! PROGRAM Province }}
            {{#each this.customProgramFieldRows as |row|}}
              <div class="flex py-4 px-4">
                <div class="w-56 shrink-0 tio-text-sm-medium tio-fg-secondary">{{row.label}}</div>
                <ExpandableText @text={{row.value}} class="tio-text-sm-medium tio-fg-secondary" />
              </div>
            {{/each}}

            {{! APPLICATION Province }}
            {{#each this.customApplicationFieldRows as |row|}}
              <div class="flex py-4 px-4">
                <div class="w-56 shrink-0 tio-text-sm-medium tio-fg-secondary">{{row.label}}</div>
                <ExpandableText @text={{row.value}} class="tio-text-sm-medium tio-fg-secondary" />
              </div>
            {{/each}}
          </div>
        {{/if}}
      </:body>
    </Section>
  </template>
}
